import type { ReactNode } from 'react'
import { usePlayground } from '../store'
import { Icons } from './TabToolbar'

interface MobileSheetProps {
    open: boolean
    onClose: () => void
    onBack?: () => void
    onReset?: () => void
    icon?: ReactNode
    title?: string
    maxHeight?: string
    children: ReactNode
}

export function MobileSheet({ open, onClose, onBack, onReset, icon, title, maxHeight: _maxHeight, children }: MobileSheetProps) {
    const isMobile = usePlayground((s) => s.isMobile)
    if (!open) return null

    const titleBar = title && (
        <div className="shrink-0" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
            <div className="flex items-center justify-between h-10 px-3">
                <div className="flex items-center gap-1">
                    {onBack ? (
                        <button onClick={onBack} className="w-8 h-8 flex items-center justify-center active:scale-[0.97]" style={{ color: 'var(--text-muted)' }}>
                            {Icons.back}
                        </button>
                    ) : icon ? (
                        <div className="w-8 h-8 flex items-center justify-center" style={{ color: 'var(--text-muted)' }}>
                            {icon}
                        </div>
                    ) : null}
                    <span className="text-xs font-bold font-mono uppercase tracking-widest leading-none" style={{ color: 'var(--text-muted)' }}>{title}</span>
                </div>
                <div className="flex items-center">
                    {onReset && (
                        <button onClick={onReset} className="h-8 px-3 text-xs font-medium font-mono active:scale-[0.97]" style={{ color: 'var(--text-muted)' }}>Reset</button>
                    )}
                    <button onClick={onClose} className="w-8 h-8 flex items-center justify-center active:scale-[0.97]" style={{ color: 'var(--text-muted)' }}>
                        {Icons.close}
                    </button>
                </div>
            </div>
        </div>
    )

    const content = (
        <div className="flex-1 overflow-y-auto px-3 pt-3 pb-4">
            {children}
        </div>
    )

    // Desktop: centered dialog
    if (!isMobile) {
        return (
            <>
                <div className="fixed inset-0 bg-black/40 z-[100]" onClick={onClose} />
                <div className="fixed z-[101] rounded-2xl border flex flex-col shadow-2xl"
                    style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '340px', maxHeight: '70vh', background: 'var(--surface-elevated)', borderColor: 'var(--border)' }}>
                    {titleBar}
                    {content}
                </div>
            </>
        )
    }

    // Mobile: bottom sheet
    return (
        <>
            <div className="fixed inset-0 bg-black/50 z-[100]" onClick={onClose} />
            <div className="fixed left-0 right-0 z-[101] rounded-t-[20px] border-t flex flex-col"
                style={{ bottom: 'calc(48px + env(safe-area-inset-bottom, 0px))', maxHeight: 'calc(100dvh - 44px - 48px - env(safe-area-inset-bottom, 0px))', background: 'var(--surface-elevated)', borderColor: 'var(--border)' }}>
                <div className="flex justify-center pt-3 pb-1 shrink-0">
                    <div className="w-10 h-1 rounded-full" style={{ background: 'var(--border)' }} />
                </div>
                {titleBar}
                {content}
            </div>
        </>
    )
}
